import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTheme } from '../../contexts/ThemeContext.jsx';

/**
 * Knowledge Base filters component for search and filtering
 */
const KnowledgeBaseFilters = ({
  filters,
  onChange,
  loading
}) => {
  const { getToken } = useAuth();
  const { theme } = useTheme();
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  /**
   * Fetch users for filter dropdown
   */
  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const token = await getToken();
      
      const response = await fetch('https://parrotmate.com/api/admin/users?limit=100&sortBy=first_name&sortOrder=asc', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  /**
   * Handle filter change
   */
  const handleFilterChange = (field, value) => {
    onChange({ [field]: value });
  };

  /**
   * Clear all filters
   */
  const clearFilters = () => {
    onChange({
      search: '',
      status: 'all',
      user: '',
      hasAssociations: 'all',
      sortBy: 'updated_at',
      sortOrder: 'desc'
    });
  };

  // Load users when component mounts
  useEffect(() => {
    fetchUsers();
  }, []);

  return (
    <div className={`rounded-lg border p-4 space-y-4 ${
      theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
    }`}>
      <div className="flex justify-between items-center">
        <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Filters</h3>
        <button
          onClick={clearFilters}
          className={`text-sm underline transition-colors ${
            theme === 'dark' 
              ? 'text-teal-400 hover:text-teal-300' 
              : 'text-teal-600 hover:text-teal-700'
          }`}
        >
          Clear All
        </button>
      </div>

      {/* Search */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
          Search
        </label>
        <input
          type="text"
          value={filters.search}
          onChange={(e) => handleFilterChange('search', e.target.value)}
          placeholder="Search by name or content..."
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
            theme === 'dark'
              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
          }`}
          disabled={loading}
        />
      </div>

      {/* Status Filter */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
          Status
        </label>
        <select
          value={filters.status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
            theme === 'dark'
              ? 'bg-gray-700 border-gray-600 text-white'
              : 'bg-white border-gray-300 text-gray-900'
          }`}
          disabled={loading}
        >
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="processing">Processing</option>
          <option value="error">Error</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* User Filter */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
          Owner
        </label>
        <select
          value={filters.user}
          onChange={(e) => handleFilterChange('user', e.target.value)}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
            theme === 'dark'
              ? 'bg-gray-700 border-gray-600 text-white'
              : 'bg-white border-gray-300 text-gray-900'
          }`}
          disabled={loading || loadingUsers}
        >
          <option value="">All Users</option>
          {users.map((user) => (
            <option key={user.id} value={user.id}>
              {user.first_name && user.last_name 
                ? `${user.first_name} ${user.last_name}`
                : user.email
              }
            </option>
          ))}
        </select>
        {loadingUsers && (
          <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Loading users...</p>
        )}
      </div>

      {/* Association Filter */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
          Parrot Associations
        </label>
        <select
          value={filters.hasAssociations}
          onChange={(e) => handleFilterChange('hasAssociations', e.target.value)}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
            theme === 'dark'
              ? 'bg-gray-700 border-gray-600 text-white'
              : 'bg-white border-gray-300 text-gray-900'
          }`}
          disabled={loading}
        >
          <option value="all">All Files</option>
          <option value="yes">With Associations</option>
          <option value="no">Without Associations</option>
        </select>
      </div>

      {/* Sort Options */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
          Sort By
        </label>
        <select
          value={filters.sortBy}
          onChange={(e) => handleFilterChange('sortBy', e.target.value)}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 mb-2 ${
            theme === 'dark'
              ? 'bg-gray-700 border-gray-600 text-white'
              : 'bg-white border-gray-300 text-gray-900'
          }`}
          disabled={loading}
        >
          <option value="updated_at">Last Updated</option>
          <option value="created_at">Created Date</option>
          <option value="name">Name</option>
          <option value="user_name">Owner</option>
          <option value="file_size">File Size</option>
        </select>
        
        <select
          value={filters.sortOrder}
          onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
            theme === 'dark'
              ? 'bg-gray-700 border-gray-600 text-white'
              : 'bg-white border-gray-300 text-gray-900'
          }`}
          disabled={loading}
        >
          <option value="desc">Descending</option>
          <option value="asc">Ascending</option>
        </select>
      </div>

      {/* Filter Summary */}
      <div className={`pt-4 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
        <h4 className={`text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Active Filters</h4>
        <div className="space-y-1 text-xs">
          {filters.search && (
            <div className="flex justify-between">
              <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Search:</span>
              <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{filters.search}</span>
            </div>
          )}
          {filters.status !== 'all' && (
            <div className="flex justify-between">
              <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Status:</span>
              <span className={`capitalize ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{filters.status}</span>
            </div>
          )}
          {filters.user && (
            <div className="flex justify-between">
              <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Owner:</span>
              <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                {users.find(u => u.id === filters.user)?.first_name || 'Selected User'}
              </span>
            </div>
          )}
          {filters.hasAssociations !== 'all' && (
            <div className="flex justify-between">
              <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Associations:</span>
              <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                {filters.hasAssociations === 'yes' ? 'With' : 'Without'} Associations
              </span>
            </div>
          )}
          <div className="flex justify-between">
            <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Sort:</span>
            <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
              {filters.sortBy.replace('_', ' ')} ({filters.sortOrder})
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBaseFilters;
